// ---------------------------------------------------------------------------
// door.ts — Visual angle interpolation for replicated door states.
// Pure transform helper — no Three.js / DOM imports — so this module is
// usable from animation.ts, the InteractiveElementManager, tests, and any
// future Web Worker that needs to predict the door pose.
//
// Networking contract (Protocol v7, physics_findings.md §4.2):
//   - The server replicates `state ∈ {Closed, Opening, Open, Closing}`
//     plus a `state_start` server timestamp and the door's `anim_time`.
//   - Collision uses the discrete `state` value — server is sole authority.
//   - The client interpolates the *visual* mesh angle from
//       progress = clamp((now - state_start) / anim_time, 0, 1)
//     and maps progress → angle for the active state direction.
//
// The 0.4 s union-AABB during the swing covers any visual-vs-collision
// drift on the server side; on the client this interpolator is the only
// thing that drives the rendered angle, so there is no separate "snap on
// completion" code path.
// ---------------------------------------------------------------------------

/** Discrete door state — must match server `DoorState` constants and the
 * `is_on_ladder`/`is_mantling`-style enum on InteractiveElementManager. */
export enum DoorVisualState {
  Closed = 0,
  Opening = 1,
  Open = 2,
  Closing = 3,
}

/** Replicated door state captured from the v7 WorldSnapshot. */
export interface DoorReplicationState {
  state: DoorVisualState;
  /** Server timestamp (seconds) when the current state began. */
  stateStart: number;
  /** Total swing time for one half-arc (Closed↔Open). */
  animTime: number;
}

/**
 * Compute the door's visual swing angle in **degrees**, clamped to
 * [0, swingAngleDeg].
 *
 *  - Closed   → 0
 *  - Open     → swingAngleDeg
 *  - Opening  → swingAngleDeg × progress
 *  - Closing  → swingAngleDeg × (1 - progress)
 *
 * `now` and `state.stateStart` must share the same time origin (server
 * clock); the caller is responsible for applying the renderer's clock
 * offset before invoking this helper.
 *
 * `animTime` is clamped to a positive floor so a malformed snapshot can't
 * divide by zero.
 */
export function computeDoorAngleDeg(
  state: DoorReplicationState,
  now: number,
  swingAngleDeg: number,
): number {
  const animTime = Math.max(state.animTime, 1e-6);
  const elapsed = Math.max(0, now - state.stateStart);
  const progress = Math.min(1, elapsed / animTime);

  switch (state.state) {
    case DoorVisualState.Closed:
      return 0;
    case DoorVisualState.Open:
      return swingAngleDeg;
    case DoorVisualState.Opening:
      return swingAngleDeg * progress;
    case DoorVisualState.Closing:
      return swingAngleDeg * (1 - progress);
    default:
      return 0;
  }
}

/** Convenience — same as computeDoorAngleDeg but returns radians. */
export function computeDoorAngleRad(
  state: DoorReplicationState,
  now: number,
  swingAngleDeg: number,
): number {
  return (computeDoorAngleDeg(state, now, swingAngleDeg) * Math.PI) / 180;
}
